/**
 * FinanceHubView
 * Single entry point for all finance workflows: Ledger, Reconciliation, Invoices
 *
 * Critical rule: UI does not compute money. All totals must come from backend summaries.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
} from "@mui/material";
import { AccountBalance, ReceiptLong, SyncAlt, Search, Payments } from "@mui/icons-material";
import { useAuth } from "@/contexts/AuthContext.jsx";
import {
  PageShell,
  PageHeader,
  PageTabs,
} from "@/shared/components/pageShell";
import PayoutRunsPanel from "../components/PayoutRunsPanel.jsx";
import LedgerSearchAutocomplete from "../components/LedgerSearchAutocomplete.jsx";
import { FINANCE_CARD } from "../../compensation/copy/opsDescriptions.js";

const detectLedgerType = (id) => {
  const value = String(id || "").trim().toUpperCase();
  if (!value) return null;
  if (value.startsWith("INV-") || value.startsWith("INV")) return "invoice";
  if (value.startsWith("BUB-") || value.startsWith("JOB-") || value.startsWith("JOB")) return "job";
  // Default to job ledger (most common) for unknown IDs
  return "job";
};

export default function FinanceHubView() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState(0);
  const [ledgerId, setLedgerId] = useState("");
  const [ledgerType, setLedgerType] = useState("auto"); // auto | job | invoice

  // Check if user has finance audit permission
  const canAuditLedger = hasPermission("finance:audit");
  const canViewPayouts = hasPermission("payouts:view");
  const canExecutePayouts = hasPermission("payouts:execute");
  const showPayoutsTab = canViewPayouts || canExecutePayouts;

  const trimmedLedgerId = useMemo(() => String(ledgerId || "").trim(), [ledgerId]);
  const effectiveType = useMemo(() => {
    if (ledgerType === "auto") return detectLedgerType(trimmedLedgerId);
    return ledgerType;
  }, [ledgerType, trimmedLedgerId]);

  const handleOpenLedger = () => {
    const id = trimmedLedgerId;
    if (!id) return;
    const encoded = encodeURIComponent(id);
    if (effectiveType === "invoice") {
      navigate(`/finance/invoices/${encoded}/ledger`);
      return;
    }
    navigate(`/finance/jobs/${encoded}/ledger`);
  };

  return (
    <PageShell sx={{ maxWidth: 1400, mx: "auto" }}>
      <PageHeader
        title="Finance"
        subtitle="Reconciliation, invoices, and ledgers — one place."
      />

      <PageTabs
        value={Math.min(tab, showPayoutsTab ? 4 : 3)}
        onChange={(_, v) => setTab(v)}
      >
        <Tab label="Overview" />
        <Tab label="Ledgers" />
        <Tab label="Reconciliation" />
        <Tab label="Invoices" />
        {showPayoutsTab && <Tab label="Payouts" />}
      </PageTabs>

      {tab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            This hub is the canonical entry point for finance. Money totals should always come from backend summaries
            derived from the ledger.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Search fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Open a ledger
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Jump directly to a Job or Invoice ledger by ID.
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      variant={ledgerType === "auto" ? "contained" : "outlined"}
                      onClick={() => setLedgerType("auto")}
                      sx={{ textTransform: "none" }}
                    >
                      Auto
                    </Button>
                    <Button
                      size="small"
                      variant={ledgerType === "job" ? "contained" : "outlined"}
                      onClick={() => setLedgerType("job")}
                      sx={{ textTransform: "none" }}
                    >
                      Job
                    </Button>
                    <Button
                      size="small"
                      variant={ledgerType === "invoice" ? "contained" : "outlined"}
                      onClick={() => setLedgerType("invoice")}
                      sx={{ textTransform: "none" }}
                    >
                      Invoice
                    </Button>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <LedgerSearchAutocomplete
                        value={ledgerId}
                        onChange={(id) => setLedgerId(id || "")}
                        ledgerType={ledgerType}
                        label="Job ID or Invoice ID"
                        placeholder="e.g. BUB-20260128-001 or INV-20260128-001"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleOpenLedger();
                        }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AccountBalance />}
                      onClick={handleOpenLedger}
                      disabled={!trimmedLedgerId}
                      sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                    >
                      Open
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Routing target:{" "}
                    {effectiveType === "invoice"
                      ? "/finance/invoices/:invoiceId/ledger"
                      : "/finance/jobs/:jobId/ledger"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <SyncAlt fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Reconciliation queue
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload FNB Gold Business PDFs, then match customer receipts to invoices/jobs.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<SyncAlt />}
                    onClick={() => navigate("/finance/reconciliation")}
                    sx={{ textTransform: "none" }}
                  >
                    Open reconciliation
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ReceiptLong fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Invoices
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    View and manage invoices (ledger-backed payments & balances).
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ReceiptLong />}
                    onClick={() => navigate("/invoices")}
                    sx={{ textTransform: "none" }}
                  >
                    Go to invoices
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Payments fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {FINANCE_CARD.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {FINANCE_CARD.body} Create plans from the order: More → Compensation & recovery.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Payments />}
                    onClick={() => navigate("/finance/compensation/due")}
                    sx={{ textTransform: "none" }}
                  >
                    {FINANCE_CARD.action}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Ledgers
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Open job and invoice ledgers. This is the system-of-record view (read-only).
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
              <Button
                size="small"
                variant={ledgerType === "auto" ? "contained" : "outlined"}
                onClick={() => setLedgerType("auto")}
                sx={{ textTransform: "none" }}
              >
                Auto
              </Button>
              <Button
                size="small"
                variant={ledgerType === "job" ? "contained" : "outlined"}
                onClick={() => setLedgerType("job")}
                sx={{ textTransform: "none" }}
              >
                Job
              </Button>
              <Button
                size="small"
                variant={ledgerType === "invoice" ? "contained" : "outlined"}
                onClick={() => setLedgerType("invoice")}
                sx={{ textTransform: "none" }}
              >
                Invoice
              </Button>
            </Box>

            <Box display="flex" gap={1} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <LedgerSearchAutocomplete
                  value={ledgerId}
                  onChange={(id) => setLedgerId(id || "")}
                  ledgerType={ledgerType}
                  label="Job ID or Invoice ID"
                  placeholder="e.g. BUB-20260128-001 or INV-20260128-001"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleOpenLedger();
                  }}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<AccountBalance />}
                onClick={handleOpenLedger}
                disabled={!trimmedLedgerId}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Open ledger
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Reconciliation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Bank transaction queue and manual matching.
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<SyncAlt />}
                onClick={() => navigate("/finance/reconciliation")}
                sx={{ textTransform: "none" }}
              >
                Open reconciliation queue
              </Button>
              {canAuditLedger && (
                <Button
                  variant="outlined"
                  startIcon={<AccountBalance />}
                  onClick={() => navigate("/finance/ledger-reconciliation")}
                  sx={{ textTransform: "none" }}
                >
                  Ledger reconciliation audit
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {tab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Invoices
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create and manage invoices, then drill into invoice ledgers for payments and allocations.
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<ReceiptLong />}
                onClick={() => navigate("/invoices")}
                sx={{ textTransform: "none" }}
              >
                Open invoices
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/invoices/new")}
                sx={{ textTransform: "none" }}
              >
                Create invoice
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {tab === 4 && showPayoutsTab && (
        <Box>
          <PayoutRunsPanel />
          {canViewPayouts && (
            <Card sx={{ mt: { xs: 2, md: 3 } }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Payout History
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  View past payout runs, filter by payee, and drill into detailed breakdowns.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AccountBalance />}
                  onClick={() => navigate("/finance/payouts/history")}
                  sx={{ textTransform: "none" }}
                >
                  View Payout History
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </PageShell>
  );
}

